/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { CARD_TYPES, BAR_CHART_TYPES } from 'carbon-addons-iot-react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import moment from 'moment';
import { defineMessages } from 'react-intl';

import {
  CUSTOM_CARD_TYPES,
  DASHBOARD_DATA_TYPES,
  DATAITEM_TAG_ALERT,
  DATAITEM_TYPE_DIMENSION,
} from '../../Common/constants/constants';
import { getLegacyDashboardCardData, getImage } from '../services/DashboardAPIs';
import { formatErrorResponse } from '../../Common/utils/responseFormatterFunctions';
import { getDomainsAPI } from '../../Setup/services/DomainAPI';
import {
  DataSourceAttribute,
  CardTypes,
  DataSource,
  Card,
  CardWithError,
  Threshold,
} from '../types/dashboardTypes';
import { DataItemType } from '../types/entitiesTypes';

import { validateCardAttributesWithGroupBy } from './dashboardUtils';

import { KPIFunctionDtoType } from 'KPITypes';
import {
  isDataItemAlert,
  isDataItemBatchMetric,
  isDataItemRawMetric,
  isDataItemStreamingMetric,
} from 'dataFormatterFunctions';
import { ApiFilter, ErrorType, FormatMessageType, UserResourcesType, Version } from 'CommonTypes';
import {
  fetchHierarchyAlerts,
  getDashboardHierarchyAlertCount,
  getDashboardCardData,
} from 'HierarchiesAPI';
import { DeviceType } from 'HierarchyTypes';

export const dashboardFetcherMessages = defineMessages({
  errorV1AndV2DataItems: 'Batch metric cannot be shown with streaming metric',
});

/**
 * Builds the attribute map needed for the dashboard fetcher/API
 */
export const buildAttributeMap = (attributes: DataSourceAttribute[]): Record<string, string> =>
  Array.isArray(attributes) &&
  attributes
    .filter(attribute => attribute.aggregator) // First only filter where there are aggregators
    .reduce((attributeMap, { attribute, aggregator }) => {
      if (!attributeMap[attribute]) {
        attributeMap[attribute] = []; // eslint-disable-line no-param-reassign
      }
      attributeMap[attribute].push(aggregator);
      return attributeMap;
    }, {});

/**
 * This function translates the Card data source json to our underlying QueryData API
 * It builds the attribute output fields needed for the fetcher API
 */
export const buildAttributeOutputMap = (
  attributes: DataSourceAttribute[]
): Record<string, string> =>
  Array.isArray(attributes) &&
  attributes
    .filter(attribute => attribute.aggregator) // First only filter where there are aggregators
    .reduce((attributeMap, { attribute, id }) => {
      if (!attributeMap[attribute]) {
        attributeMap[attribute] = []; // eslint-disable-line no-param-reassign
      }
      attributeMap[attribute].push(id);
      return attributeMap;
    }, {});

/**
 * Builds just the raw attribute array request, by filtering out those without aggregators
 */
export const buildRawAttributeArray = (attributes: DataSourceAttribute[]): string[] =>
  Array.isArray(attributes) &&
  attributes.filter(attribute => isNil(attribute.aggregator)).map(attribute => attribute.attribute);

/**
 * Builds the raw attribute map which is simpler than the aggregated map
 */
export const buildRawAttributeOutputMap = (
  attributes: DataSourceAttribute[]
): Record<string, string> =>
  Array.isArray(attributes) &&
  attributes
    .filter(attribute => isNil(attribute.aggregator))
    .reduce((acc, attribute) => {
      acc[attribute.attribute] = attribute.id;
      return acc;
    }, {});

/**
 * Examines a the attributes array of a card and builds the necessary attribute data for the dashboard query data interface
 */
export const buildAttributeMaps = (
  attributes: DataSourceAttribute[],
  cardType: CardTypes,
  dataItems: DataItemType[],
  isV2DatalakeApiCall: boolean
): {
  rawAttributes: string[];
  rawAttributesOutputMap: Record<string, string>;
  aggregatedAttributeMap: Record<string, string>;
  attributeOutputMap: Record<string, string>;
} => {
  // Support default aggregators of 'last' for IMAGE and non-dimensional VALUE cards,
  // if they leave off the aggregator for TIMESERIES or TABLE cards then give them the raw data
  const attributesWithDefaultAggregators = attributes.map(attribute => {
    let isDimensionValue = false;

    const matchingDataItem = dataItems?.find(dataItem => dataItem.name === attribute.attribute);

    if (matchingDataItem) {
      isDimensionValue = matchingDataItem.type === DATAITEM_TYPE_DIMENSION;
    }

    const { attribute: dataItemAttributeName, eventName, id, aggregator } = attribute;

    let updatedDataItemAttributeName = dataItemAttributeName;

    // If we are using V1 datalake / query data API then we dont have to add eventName untill API supports it.
    if (isV2DatalakeApiCall) {
      updatedDataItemAttributeName = eventName
        ? `${dataItemAttributeName}::${eventName}` // If the eventName present in the dashboard JSON
        : matchingDataItem?.eventName // If the eventName present in the matchingDataItem
        ? `${dataItemAttributeName}::${matchingDataItem.eventName}`
        : dataItemAttributeName; // Use just data item name from dashboard JSON
    }

    return {
      id,
      attribute: updatedDataItemAttributeName,
      aggregator:
        aggregator ||
        ((cardType === CARD_TYPES.VALUE || cardType === CARD_TYPES.IMAGE) &&
        !isDimensionValue &&
        !isV2DatalakeApiCall
          ? 'last' // default metric values to last
          : undefined),
    } as DataSourceAttribute;
  });

  // raw data doesn't work inside Image cards because they can't handle multiple timestamped records
  return {
    rawAttributes: [...new Set(buildRawAttributeArray(attributesWithDefaultAggregators))],
    rawAttributesOutputMap: buildRawAttributeOutputMap(attributesWithDefaultAggregators),
    aggregatedAttributeMap: buildAttributeMap(attributesWithDefaultAggregators),
    attributeOutputMap: buildAttributeOutputMap(attributesWithDefaultAggregators),
  };
};

/**
 * Filters and returns the attributes to be mapped
 * If attributes are also defined in groupBy, filter them out so that only one fetch will occur
 * for each attribute. This fixes a bug where an extra fetch was happening
 * @param {Object} dataSource
 * @returns {Array} attributesToMap formatted with the proper restrictions
 */
export const filterAttributesToMap = (dataSource: DataSource): DataSourceAttribute[] => {
  let attributesToMap = [];
  if (dataSource) {
    // See if any attributes are included in the groupBy. If so, don't pass those attributes
    // as they will be included in the groupBy fetch
    if (dataSource.groupBy) {
      attributesToMap = dataSource.attributes.filter(attr => !dataSource.groupBy.includes(attr.id));
    } else {
      attributesToMap = dataSource.attributes;
    }
  }
  return attributesToMap;
};

export const hasV1DerivedMetric = (
  attributes: DataSourceAttribute[],
  dataItems: DataItemType[]
): boolean =>
  !isEmpty(attributes) &&
  attributes.some(item => {
    const matchingDataItem = dataItems?.find(dataItem => dataItem.name === item.attribute);
    return matchingDataItem && isDataItemBatchMetric(matchingDataItem);
  });

export const hasV2DerivedMetric = (
  attributes: DataSourceAttribute[],
  dataItems: DataItemType[]
): boolean =>
  !isEmpty(attributes) &&
  attributes.some(item => {
    const matchingDataItem = dataItems?.find(dataItem => dataItem.name === item.attribute);
    return matchingDataItem && isDataItemStreamingMetric(matchingDataItem);
  });

export const hasRawMetric = (
  attributes: DataSourceAttribute[],
  dataItems: DataItemType[]
): boolean =>
  !isEmpty(attributes) &&
  attributes.some(item => {
    const matchingDataItem = dataItems?.find(dataItem => dataItem.name === item.attribute);
    return matchingDataItem && isDataItemRawMetric(matchingDataItem);
  });

// Builds datafilters to be passed in the payload for v2 deviceTypes
export const buildDataFiltersFromCard = (card: Card): Record<string, Array<string>> => {
  let dataFilterArray = [];
  const filters = {};
  switch (card?.type) {
    case CARD_TYPES.TIMESERIES:
    case CARD_TYPES.BAR:
      dataFilterArray = card.content.series;
      break;
    case CARD_TYPES.VALUE:
      dataFilterArray = card.content.attributes;
      break;
    case CARD_TYPES.IMAGE:
      card.content?.hotspots?.forEach(
        hotspot => hotspot.content.attributes?.map(attribute => dataFilterArray.push(attribute))
      );
      break;
    default:
      return filters;
  }
  dataFilterArray?.length > 0 &&
    dataFilterArray.forEach(item => {
      // check if dataFilter is present
      if (item.dataFilter) {
        // get all the keys of dataFilter
        Object.entries(item.dataFilter).forEach(([key, value]) => {
          if (Object.keys(filters).includes(key)) {
            // if key exist then push the value for each dataItem
            filters[key].push(value);
          } else {
            // if key does not exist then create it and push the value
            filters[key] = [value];
          }
        });
      }
    });
  return filters;
};

/**
 * Uses the dataSource parameters of the card and retrieve the data for device type or device or hierarchy resources from the appropriate backend.
 * @param {Card} cardProp card properties
 * @param {string} hierarchyPath API path
 * @param {string} resourceUUID resource uuid
 * @param {string} entity device id or instance id of the hiearchy resource
 * @param {object} dimensionFilter optional dimensional filter of on summary dashboard
 * @param {moment.Moment} startDate start date and time of the fetch
 * @param {moment.Moment} endDate end date and time of the fetch
 * @param {string} grain time grain
 * @param {DataItemType[]} dataItems list of data items
 * @param {string} userEmailId user email id
 * @returns
 */
export const fetchHierarchyCardData = async (
  cardProp: Card,
  hierarchyPath: string,
  resourceUUID: string,
  entity: string,
  dimensionFilter: Record<string, ApiFilter> | null,
  startDate: moment.Moment,
  endDate: moment.Moment,
  grain: string,
  dataItems: DataItemType[],
  userEmailId: string,
  formatMessage: FormatMessageType,
  currentUserResources: UserResourcesType
): Promise<{ data; additionalData } | Card | { dataError }> => {
  const card = cardProp;
  // If there was an error setting up the card, just return back the original card
  if (card.setupError) {
    return Promise.resolve(card);
  }

  const { type, additionalData, groupBy } = card.dataSource;

  const { maximumDataPoints } = card?.content || {};

  // need to restrict more data points for stacked charts for performance
  const rawDataLimit =
    card.type === CARD_TYPES.BAR && card.content.type === BAR_CHART_TYPES.STACKED ? 1000 : 3000;

  const dataLimit = maximumDataPoints || rawDataLimit;

  const dataPromises = [];

  // Load the main data for the card, first check if we've been asked for Alert data
  if (type === DASHBOARD_DATA_TYPES.ALERT && card.type === CUSTOM_CARD_TYPES.ALERT) {
    // This type of card uses raw alerts
    dataPromises.push(
      fetchHierarchyAlerts(
        hierarchyPath,
        dimensionFilter,
        startDate.valueOf(),
        endDate.valueOf(),
        '', // groupBy
        '', // search
        !hierarchyPath.includes('devices'), // Should show children
        userEmailId,
        dataLimit,
        currentUserResources
      )
    );
  } else if (type === DASHBOARD_DATA_TYPES.ALERT) {
    dataPromises.push(
      getDashboardHierarchyAlertCount(
        hierarchyPath,
        dimensionFilter,
        startDate.valueOf(),
        endDate.valueOf(),
        groupBy?.[0] ?? 'status', // groupBy
        '', // search
        !hierarchyPath.includes('devices'), // Should show children
        userEmailId
      )
    );
  } else if (isEmpty(card.dataSource?.attributes)) {
    // if we have no attributes, we have no data to recover so just move on
    dataPromises.push(Promise.resolve([]));
  } else {
    // otherwise we're showing metric data
    const attributesToMap = filterAttributesToMap(card.dataSource);

    // To check if the attributes contains V1 derived(batch) metric
    const hasV1DerivedMetrics = hasV1DerivedMetric(attributesToMap, dataItems);
    // To check if the attributes contains V2 derived(steaming) metric
    const hasV2DerivedMetrics = hasV2DerivedMetric(attributesToMap, dataItems);

    const attributeMapsForNextLineCard = buildAttributeMaps(
      attributesToMap,
      card.type,
      dataItems,
      !hasV1DerivedMetrics // Not to call v2 datalake api if there is only V1 batch metric
    );

    let localHierarchyPath = `/workspaces/resources/${resourceUUID}`;

    if (hasV1DerivedMetrics) {
      localHierarchyPath = hierarchyPath;
    }

    let cardErrorMessage;
    if (hasV1DerivedMetrics && hasV2DerivedMetrics) {
      cardErrorMessage = formatMessage(dashboardFetcherMessages.errorV1AndV2DataItems);
    }

    dataPromises.push(
      getDashboardCardData(
        localHierarchyPath,
        attributeMapsForNextLineCard.aggregatedAttributeMap,
        attributeMapsForNextLineCard.attributeOutputMap,
        attributeMapsForNextLineCard.rawAttributes,
        attributeMapsForNextLineCard.rawAttributesOutputMap,
        entity,
        startDate,
        endDate,
        grain,
        groupBy,
        dimensionFilter,
        {}, // no datafilter for heirarchy resources
        dataLimit,
        userEmailId,
        !hasV1DerivedMetrics, // Not to call v2 datalake api if there is only V1 batch metric
        cardErrorMessage
      )
    );
  }

  // Go get additional raw alert data if our card has an additionalData section
  if (!isEmpty(additionalData) && additionalData.type === DASHBOARD_DATA_TYPES.ALERT) {
    dataPromises.push(
      fetchHierarchyAlerts(
        hierarchyPath,
        dimensionFilter,
        startDate.valueOf(),
        endDate.valueOf(),
        '', // groupBy
        '', // search
        false, // !hierarchyPath.includes('devices'), Should show children
        userEmailId,
        dataLimit,
        currentUserResources
      )
    );
  }

  // Wait for all the card data to be loaded
  return Promise.all(dataPromises)
    .then(([data, additionalDataResponse]) => ({ data, additionalData: additionalDataResponse }))
    .catch((apiError: ErrorType) => {
      console.error(apiError);
      // Set the specific error
      return { dataError: formatErrorResponse(apiError) };
    });
};

/**
 * Uses the dataSource parameters of the card and retrieve the data for device type or device from the appropriate backend.
 * @param {Card} cardProp card properties
 * @param {string} hierarchyPath API path
 * @param {string} entityType device type name
 * @param {string} entityTypeUUID device type uuid
 * @param {string} entity  device id
 * @param {DeviceType[]} deviceTypes list of device types
 * @param {object} dimensionFilter optional dimensional filter of on summary dashboard
 * @param {moment.Moment} startDate start date and time of the fetch
 * @param {moment.Moment} endDate end date and time of the fetch
 * @param {string} grain time grain
 * @param {DataItemType[]} dataItems list of data items
 * @param {string} userEmailId user email id
 * @param formatMessage i18n format message function
 * @returns
 */
export const fetchLegacyDashboardCardData = async (
  cardProp: Card,
  hierarchyPath: string,
  entityType: string,
  entityTypeUUID: string,
  entity: string,
  deviceTypes: DeviceType[],
  dimensionFilter: Record<string, ApiFilter> | null,
  startDate: moment.Moment,
  endDate: moment.Moment,
  grain: string,
  dataItems: DataItemType[],
  userEmailId: string,
  formatMessage: FormatMessageType,
  currentUserResources: UserResourcesType
): Promise<{ data; additionalData } | Card | { dataError }> => {
  const card = cardProp;
  // If there was an error setting up the card, just return back the original card
  if (card.setupError) {
    return Promise.resolve(card);
  }

  const { type, additionalData, groupBy } = card.dataSource;

  const { maximumDataPoints } = card?.content || {};

  // need to restrict more data points for stacked charts for performance
  const rawDataLimit =
    card.type === CARD_TYPES.BAR && card.content.type === BAR_CHART_TYPES.STACKED ? 1000 : 3000;

  const dataLimit = maximumDataPoints || rawDataLimit;

  const dataPromises = [];

  // Load the main data for the card, first check if we've been asked for Alert data
  if (type === DASHBOARD_DATA_TYPES.ALERT && card.type === CUSTOM_CARD_TYPES.ALERT) {
    // This type of card uses raw alerts
    dataPromises.push(
      fetchHierarchyAlerts(
        hierarchyPath,
        dimensionFilter,
        startDate.valueOf(),
        endDate.valueOf(),
        '', // groupBy
        '', // search
        false, // Should show children
        userEmailId,
        dataLimit,
        currentUserResources
      )
    );
  } else if (type === DASHBOARD_DATA_TYPES.ALERT) {
    // Otherwise, we should use aggregated alert counts
    dataPromises.push(
      getDashboardHierarchyAlertCount(
        hierarchyPath,
        dimensionFilter,
        startDate.valueOf(),
        endDate.valueOf(),
        groupBy[0] || 'status',
        '', // search
        false, // Should show children
        userEmailId
      )
    );
  } else if (isEmpty(card.dataSource?.attributes)) {
    // if we have no attributes, we have no data to recover so just move on
    dataPromises.push(Promise.resolve([]));
  } else {
    // otherwise we're showing metric data
    const attributesToMap = filterAttributesToMap(card.dataSource);

    const entityTypeName =
      card?.dataSource?.type && card?.dataSource?.type !== DASHBOARD_DATA_TYPES.ALERT
        ? card.dataSource?.type
        : entityType; // metric card might ask for different entity type data

    const deviceType = Array.isArray(deviceTypes)
      ? deviceTypes.find(item => item.name === entityTypeName)
      : undefined;

    if (deviceType?.version === Version.V2) {
      // returns all datafilters applied to dataItems
      const dataFilters = buildDataFiltersFromCard(card);

      // To check if the attributes contains V1 derived(batch) metric
      const hasV1DerivedMetrics = hasV1DerivedMetric(attributesToMap, dataItems);
      // To check if the attributes contains V2 derived(steaming) metric
      const hasV2DerivedMetrics = hasV2DerivedMetric(attributesToMap, dataItems);

      const hasV1DerivedMetricAndRawMetrics =
        hasV1DerivedMetrics && hasRawMetric(attributesToMap, dataItems);

      // Not to call v2 datalake api if there is only V1 batch metric or V1 batch + raw metric
      const isV2DatalakeApiCall = !hasV1DerivedMetrics && !hasV1DerivedMetricAndRawMetrics;

      const attributeMapsForNextLineCard = buildAttributeMaps(
        attributesToMap,
        card.type,
        dataItems,
        isV2DatalakeApiCall
      );

      let cardErrorMessage;
      if (hasV1DerivedMetrics && hasV2DerivedMetrics) {
        cardErrorMessage = formatMessage(dashboardFetcherMessages.errorV1AndV2DataItems);
      } else if (isV2DatalakeApiCall) {
        cardErrorMessage = validateCardAttributesWithGroupBy(card, dataItems, formatMessage);
      }

      let entityTypeUUIDOverride = entityTypeUUID;

      if (entityTypeName !== entityType) {
        entityTypeUUIDOverride = deviceType.uuid;
      }

      const localHierarchyPath =
        hasV1DerivedMetrics || hasV1DerivedMetricAndRawMetrics
          ? `/deviceTypes/${entityTypeUUIDOverride}` // call service libaray python API
          : `/workspaces/resources/${entityTypeUUIDOverride}`; // call new datalake java API

      dataPromises.push(
        getDashboardCardData(
          localHierarchyPath,
          attributeMapsForNextLineCard.aggregatedAttributeMap,
          attributeMapsForNextLineCard.attributeOutputMap,
          attributeMapsForNextLineCard.rawAttributes,
          attributeMapsForNextLineCard.rawAttributesOutputMap,
          entity,
          startDate,
          endDate,
          grain,
          groupBy,
          dimensionFilter,
          dataFilters,
          dataLimit,
          userEmailId,
          isV2DatalakeApiCall,
          cardErrorMessage
        )
      );
    } else {
      const attributeMapsForNextLineCard = buildAttributeMaps(
        attributesToMap,
        card.type,
        dataItems,
        false
      );

      dataPromises.push(
        getLegacyDashboardCardData(
          attributeMapsForNextLineCard.aggregatedAttributeMap,
          attributeMapsForNextLineCard.attributeOutputMap,
          attributeMapsForNextLineCard.rawAttributes,
          attributeMapsForNextLineCard.rawAttributesOutputMap,
          entity,
          entityTypeName,
          startDate,
          endDate,
          grain,
          groupBy,
          dimensionFilter,
          dataLimit,
          Boolean(maximumDataPoints) // sample or not
        )
      );
    }
  }

  // Go get additional raw alert data if our card has an additionalData section
  if (!isEmpty(additionalData) && additionalData.type === DASHBOARD_DATA_TYPES.ALERT) {
    let searchFilter = '';
    // adding name filter to reduce and get the only needed alerts by alert/dataitem name
    if (additionalData?.dataFilter?.name && Array.isArray(additionalData.dataFilter.name)) {
      searchFilter = additionalData.dataFilter.name.join('|');
    }

    dataPromises.push(
      fetchHierarchyAlerts(
        hierarchyPath,
        dimensionFilter,
        startDate.valueOf(),
        endDate.valueOf(),
        '', // groupBy
        searchFilter, // search
        false, // Should show children
        userEmailId,
        dataLimit,
        currentUserResources
      )
    );
  }

  // Wait for all the card data to be loaded
  return Promise.all(dataPromises)
    .then(([data, additionalDataResponse]) => ({ data, additionalData: additionalDataResponse }))
    .catch((apiError: ErrorType) => {
      console.error(apiError);
      // Set the specific error
      return { dataError: formatErrorResponse(apiError) };
    });
};

/** updates the card with an error from the api layer */
export const updateCardWithAPIError = (card: Card, apiError: ErrorType): CardWithError => {
  console.error(apiError);
  return {
    ...card,
    isLoading: false,
    setupError: formatErrorResponse(apiError),
  };
};

/** fetch the image from the backend */
export const fetchImage = (
  card: Card,
  entityType: string,
  entity: string
): Promise<Card | CardWithError> =>
  getImage(entityType, entity, card.content.id || card.content.image)
    .then(imageContent => ({
      ...card,
      content: {
        ...card.content,
        src: imageContent,
      },
    }))
    .catch(apiError => updateCardWithAPIError(card, apiError));

/**
 * This function will only work for our out of the box alert functions because it has a hard dependency on the threshold and severity input names
 *
 * @param {object} {input: {upper_threshold: value, lower_threshold: value, Severity: value}} kpiDetails details of the kpi which includes the threshold values and severity of the alert
 * @param {string} alertname name of the alert
 * @param {array<object>} severityDomain list of severities needed to determine the color of the alert, each severity has a value and iconColoor
 */
export const determineThresholdsOfKPI = (
  kpiDetails: KPIFunctionDtoType,
  alertname: string,
  severityDomain: { value: string; iconColor: string }[] = []
): Array<Threshold> => {
  const upperThresholdValue = !isNil(kpiDetails?.input?.upper_threshold)
    ? kpiDetails?.input?.upper_threshold
    : null;
  const lowerThresholdValue = !isNil(kpiDetails?.input?.lower_threshold)
    ? kpiDetails?.input?.lower_threshold
    : null;
  const severityColor = Array.isArray(severityDomain)
    ? severityDomain.find(severity => kpiDetails?.input?.Severity === severity.value)?.iconColor ||
      'red'
    : 'red';

  // if no output_alert_upper exists, then it's a simple alert function
  return [
    ...(!isNil(upperThresholdValue)
      ? [
          {
            axis: 'y',
            comparison: 'greaterThan',
            value: upperThresholdValue,
            label: alertname,
            fillColor: severityColor,
          },
        ]
      : []),
    // if no output_alert_lower exists, then it's a simple alert function
    ...(!isNil(lowerThresholdValue)
      ? [
          {
            axis: 'y',
            comparison: 'lessThan',
            value: lowerThresholdValue,
            label: alertname,
            fillColor: severityColor,
          },
        ]
      : []),
  ];
};

/**
 * Async function that returns possible thresholds for a dashboardeditor
 * @param {DataItemType[]} dataItems an array of data items for this entityType
 * @returns
 */
export const fetchAlertThresholds = async (
  dataItems: DataItemType[] = []
): Promise<Array<Threshold>> => {
  const alertDataItems = dataItems?.filter(
    dataItem => isDataItemAlert(dataItem) || dataItem.tags.includes(DATAITEM_TAG_ALERT)
  );

  if (!isEmpty(alertDataItems)) {
    // Need to load the severity domains so we can figure out the color.
    return getDomainsAPI().then(domains =>
      // returns the updated card
      Promise.all(
        alertDataItems.map(async thisAlertName => {
          const thisAlertThresholds = determineThresholdsOfKPI(
            thisAlertName?.kpiFunctionDto,
            thisAlertName.name,
            domains?.Severity
          );
          return thisAlertThresholds || [];
        })
      ).then(arrayOfThresholdArrays => flatten(arrayOfThresholdArrays))
    );
  }
  return Promise.resolve([]);
};
